import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { examApi, classesApi, subjectsApi } from '../../services/api';
import { toast } from 'sonner';
import {
  Plus, Search, Pencil, Trash2, BookOpen, Loader2, ShieldCheck, Sparkles,
} from 'lucide-react';
import AIGenerateQuestionsDialog from './AIGenerateQuestionsDialog';

const QUESTION_TYPES = ['MCQ', 'Short', 'Long', 'Case Study', 'True/False', 'Fill in the Blank'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];

const emptyQuestion = {
  subject_id: '',
  class_id: '',
  chapter: '',
  topic: '',
  question_type: 'Short',
  question_text: '',
  marks: 2,
  difficulty: 'Medium',
  learning_outcome: '',
  competency_tag: '',
  options: ['', '', '', ''],
  correct_answer: '',
  explanation: '',
};

const QuestionBankPage = () => {
  const [questions, setQuestions] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [fClass, setFClass] = useState('all');
  const [fSubject, setFSubject] = useState('all');
  const [fType, setFType] = useState('all');
  const [fDifficulty, setFDifficulty] = useState('all');
  const [search, setSearch] = useState('');

  // Modal
  const [open, setOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyQuestion);
  const [saving, setSaving] = useState(false);

  const loadMeta = async () => {
    try {
      const [c, s] = await Promise.all([classesApi.getAll(), subjectsApi.getAll()]);
      setClasses(c.data.classes || []);
      setSubjects(s.data.subjects || []);
    } catch (e) {
      console.error(e);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fClass !== 'all') params.class_id = fClass;
      if (fSubject !== 'all') params.subject_id = fSubject;
      if (fType !== 'all') params.question_type = fType;
      if (fDifficulty !== 'all') params.difficulty = fDifficulty;
      if (search) params.search = search;
      const { data } = await examApi.listQuestions(params);
      setQuestions(data.questions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load question bank');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); }, [fClass, fSubject, fType, fDifficulty]);  // eslint-disable-line react-hooks/exhaustive-deps

  const filteredSubjects = useMemo(() => {
    if (fClass === 'all') return subjects;
    return subjects.filter(s => s.class_id === fClass);
  }, [subjects, fClass]);

  const formSubjects = useMemo(() => {
    if (!form.class_id) return subjects;
    return subjects.filter(s => s.class_id === form.class_id);
  }, [subjects, form.class_id]);

  const openNew = () => {
    setEditId(null);
    setForm(emptyQuestion);
    setOpen(true);
  };

  const openEdit = (q) => {
    setEditId(q.id);
    setForm({
      ...emptyQuestion,
      ...q,
      options: q.options && q.options.length ? q.options : ['', '', '', ''],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.subject_id || !form.class_id || !form.question_text || !form.question_type) {
      toast.error('Class, Subject, Type and Question Text are required');
      return;
    }
    const payload = { ...form, marks: parseFloat(form.marks) };
    if (payload.question_type !== 'MCQ') payload.options = null;
    setSaving(true);
    try {
      if (editId) {
        await examApi.updateQuestion(editId, payload);
        toast.success('Question updated');
      } else {
        await examApi.createQuestion(payload);
        toast.success('Question added to bank');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this question from the bank?')) return;
    try {
      await examApi.deleteQuestion(id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="question-bank-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-primary" /> Question Bank
          </h1>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <ShieldCheck className="h-3.5 w-3.5" /> Confidential · Role-restricted access
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAiOpen(true)} data-testid="ai-generate-btn-open">
            <Sparkles className="h-4 w-4 mr-2 text-primary" /> Generate with AI
          </Button>
          <Button onClick={openNew} data-testid="add-question-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Question
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Class</Label>
            <Select value={fClass} onValueChange={setFClass}>
              <SelectTrigger data-testid="filter-class"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {classes.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Subject</Label>
            <Select value={fSubject} onValueChange={setFSubject}>
              <SelectTrigger data-testid="filter-subject"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {filteredSubjects.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={fType} onValueChange={setFType}>
              <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {QUESTION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Difficulty</Label>
            <Select value={fDifficulty} onValueChange={setFDifficulty}>
              <SelectTrigger data-testid="filter-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs">Search</Label>
            <div className="flex gap-2">
              <Input
                placeholder="search question text..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && load()}
                data-testid="filter-search"
              />
              <Button variant="outline" onClick={load} data-testid="search-btn">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {loading ? 'Loading…' : `${questions.length} question${questions.length === 1 ? '' : 's'}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : questions.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              No questions yet. Add your first one above.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Question</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Class / Subject</TableHead>
                    <TableHead>Marks</TableHead>
                    <TableHead>Difficulty</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {questions.map(q => {
                    const cls = classes.find(c => c.id === q.class_id);
                    const sub = subjects.find(s => s.id === q.subject_id);
                    return (
                      <TableRow key={q.id} data-testid={`question-row-${q.id}`}>
                        <TableCell className="max-w-xl">
                          <p className="truncate">{q.question_text}</p>
                          {q.chapter && <p className="text-xs text-muted-foreground">Ch: {q.chapter}</p>}
                        </TableCell>
                        <TableCell><Badge variant="outline">{q.question_type}</Badge></TableCell>
                        <TableCell className="text-sm">
                          {cls?.name || '—'}<br />
                          <span className="text-muted-foreground">{sub?.name || '—'}</span>
                        </TableCell>
                        <TableCell>{q.marks}</TableCell>
                        <TableCell>
                          <Badge variant={
                            q.difficulty === 'Hard' ? 'destructive'
                              : q.difficulty === 'Easy' ? 'secondary' : 'default'
                          }>{q.difficulty}</Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button size="sm" variant="outline" onClick={() => openEdit(q)}
                                  data-testid={`edit-question-${q.id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(q.id)}
                                  data-testid={`delete-question-${q.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Question' : 'Add Question to Bank'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Class *</Label>
                <Select value={form.class_id} onValueChange={(v) => setForm(f => ({ ...f, class_id: v, subject_id: '' }))}>
                  <SelectTrigger data-testid="q-class"><SelectValue placeholder="Choose class" /></SelectTrigger>
                  <SelectContent>
                    {classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject *</Label>
                <Select value={form.subject_id} onValueChange={(v) => setForm(f => ({ ...f, subject_id: v }))}>
                  <SelectTrigger data-testid="q-subject"><SelectValue placeholder="Choose subject" /></SelectTrigger>
                  <SelectContent>
                    {formSubjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Chapter</Label>
                <Input value={form.chapter} onChange={e => setForm(f => ({ ...f, chapter: e.target.value }))}
                       data-testid="q-chapter" />
              </div>
              <div>
                <Label>Topic</Label>
                <Input value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                       data-testid="q-topic" />
              </div>
              <div>
                <Label>Type *</Label>
                <Select value={form.question_type} onValueChange={(v) => setForm(f => ({ ...f, question_type: v }))}>
                  <SelectTrigger data-testid="q-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Difficulty *</Label>
                <Select value={form.difficulty} onValueChange={(v) => setForm(f => ({ ...f, difficulty: v }))}>
                  <SelectTrigger data-testid="q-difficulty"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Marks *</Label>
                <Input type="number" min="0.5" step="0.5" value={form.marks}
                       onChange={e => setForm(f => ({ ...f, marks: e.target.value }))}
                       data-testid="q-marks" />
              </div>
              <div>
                <Label>Learning Outcome</Label>
                <Input value={form.learning_outcome}
                       onChange={e => setForm(f => ({ ...f, learning_outcome: e.target.value }))}
                       data-testid="q-lo" />
              </div>
            </div>
            <div>
              <Label>Question Text *</Label>
              <Textarea rows={3} value={form.question_text}
                        onChange={e => setForm(f => ({ ...f, question_text: e.target.value }))}
                        data-testid="q-text" />
            </div>
            {form.question_type === 'MCQ' && (
              <div className="grid grid-cols-2 gap-2">
                {[0, 1, 2, 3].map(i => (
                  <div key={i}>
                    <Label className="text-xs">Option {String.fromCharCode(97 + i)}</Label>
                    <Input value={form.options[i] || ''}
                           onChange={e => {
                             const opts = [...form.options];
                             opts[i] = e.target.value;
                             setForm(f => ({ ...f, options: opts }));
                           }}
                           data-testid={`q-opt-${i}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Correct Answer (private)</Label>
                <Input value={form.correct_answer}
                       onChange={e => setForm(f => ({ ...f, correct_answer: e.target.value }))}
                       data-testid="q-answer" />
              </div>
              <div>
                <Label>Competency Tag</Label>
                <Input value={form.competency_tag}
                       onChange={e => setForm(f => ({ ...f, competency_tag: e.target.value }))}
                       data-testid="q-tag" />
              </div>
            </div>
            <div>
              <Label>Explanation (private, optional)</Label>
              <Textarea rows={2} value={form.explanation}
                        onChange={e => setForm(f => ({ ...f, explanation: e.target.value }))}
                        data-testid="q-explanation" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-question-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editId ? 'Update' : 'Save to Bank'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIGenerateQuestionsDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        onSaved={load}
      />
    </div>
  );
};

export default QuestionBankPage;
